<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';

import CompaniesListLayout from 'dashboard/components-next/Companies/CompaniesListLayout.vue';
import CompaniesTable from 'dashboard/components-next/Companies/CompaniesTable.vue';
import CreateNewCompanyDialog from 'dashboard/components-next/Companies/CompaniesForm/CreateNewCompanyDialog.vue';
import CompaniesBulkActionBar from '../components/CompaniesBulkActionBar.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const DEBOUNCE_DELAY = 300;

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const searchQuery = computed(() => route.query?.search || '');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const activeSort = computed(() => {
  const sortParam = route.query?.sort || 'name';
  return sortParam.startsWith('-') ? sortParam.slice(1) : sortParam;
});

const activeOrdering = computed(() => {
  const sortParam = route.query?.sort || 'name';
  return sortParam.startsWith('-') ? '-' : '';
});

// Filters state
const activeFilters = ref({
  company_type: [],
  status: [],
  industry: [],
});

const companies = useMapGetter('companies/getCompaniesList');
const meta = useMapGetter('companies/getMeta');
const uiFlags = useMapGetter('companies/getUIFlags');

const isFetchingList = computed(() => uiFlags.value.fetchingList);

const sortParam = computed(() => {
  return activeOrdering.value === '-'
    ? `-${activeSort.value}`
    : activeSort.value;
});

const hasCompanies = computed(() => companies.value.length > 0);

const hasActiveFilters = computed(() => {
  return Object.values(activeFilters.value).some(v => v && v.length > 0);
});

// Bulk selection state
const selectedCompanyIds = ref([]);
const isBulkActionLoading = ref(false);
const bulkDeleteDialogRef = ref(null);

const selectedCount = computed(() => selectedCompanyIds.value.length);
const hasSelection = computed(() => selectedCount.value > 0);

const visibleCompanyIds = computed(() =>
  companies.value.map(company => company.id)
);

const bulkDeleteDialogTitle = computed(() =>
  selectedCount.value > 1
    ? t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.TITLE')
    : t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_TITLE')
);

const bulkDeleteDialogDescription = computed(() =>
  selectedCount.value > 1
    ? t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.DESCRIPTION', {
        count: selectedCount.value,
      })
    : t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.SINGULAR_DESCRIPTION')
);

const bulkDeleteDialogConfirmLabel = computed(() =>
  selectedCount.value > 1
    ? t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_MULTIPLE')
    : t('COMPANIES_BULK_ACTIONS.DELETE_DIALOG.CONFIRM_SINGLE')
);

const clearSelection = () => {
  selectedCompanyIds.value = [];
};

const toggleSelectAll = shouldSelect => {
  selectedCompanyIds.value = shouldSelect ? [...visibleCompanyIds.value] : [];
};

const toggleCompanySelection = ({ id, value }) => {
  const isAlreadySelected = selectedCompanyIds.value.includes(id);
  const shouldSelect = value ?? !isAlreadySelected;

  if (shouldSelect && !isAlreadySelected) {
    selectedCompanyIds.value = [...selectedCompanyIds.value, id];
  } else if (!shouldSelect && isAlreadySelected) {
    selectedCompanyIds.value = selectedCompanyIds.value.filter(
      companyId => companyId !== id
    );
  }
};

const openBulkDeleteDialog = () => {
  if (!selectedCompanyIds.value.length || isBulkActionLoading.value) return;
  bulkDeleteDialogRef.value?.open?.();
};

// eslint-disable-next-line no-use-before-define
const deleteCompanies = async () => {
  if (!selectedCompanyIds.value.length) return;

  isBulkActionLoading.value = true;
  try {
    await Promise.all(
      selectedCompanyIds.value.map(id =>
        store.dispatch('companies/deleteCompany', id)
      )
    );
    useAlert(
      t('COMPANIES_BULK_ACTIONS.DELETE_SUCCESS', {
        count: selectedCompanyIds.value.length,
      })
    );
    clearSelection();
    bulkDeleteDialogRef.value?.close?.();
    // eslint-disable-next-line no-use-before-define
    fetchCompanies(1);
  } catch (error) {
    useAlert(t('COMPANIES_BULK_ACTIONS.DELETE_FAILED'));
  } finally {
    isBulkActionLoading.value = false;
  }
};

const generateCSV = data => {
  const headers = [
    'ID',
    'Name',
    'Domain',
    'Email',
    'Phone',
    'Industry',
    'Company Type',
    'Status',
    'Description',
    'Created At',
  ];

  const rows = data.map(company => [
    company.id,
    company.name || '',
    company.domain || '',
    company.email || '',
    company.phone || '',
    company.industry || '',
    company.companyType || '',
    company.status || '',
    company.description?.replace(/[\n\r,]/g, ' ') || '',
    company.created_at || '',
  ]);

  return [headers, ...rows].map(row => row.join(',')).join('\n');
};

const downloadCSV = (content, filename) => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const exportSelectedCompanies = () => {
  if (!selectedCompanyIds.value.length) return;

  const selectedData = companies.value.filter(company =>
    selectedCompanyIds.value.includes(company.id)
  );

  const csvContent = generateCSV(selectedData);
  downloadCSV(csvContent, 'companies_export.csv');
  useAlert(
    t('COMPANIES_BULK_ACTIONS.EXPORT_SUCCESS', {
      count: selectedCompanyIds.value.length,
    })
  );
};

const updateURLParams = (page, search = '', sort = '', filters = {}) => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  // Add filter params to URL
  Object.entries(filters).forEach(([key, values]) => {
    if (values && values.length > 0) {
      query[key] = values.join(',');
    } else {
      delete query[key];
    }
  });

  router.replace({ query });
};

const buildFilterParams = () => {
  const params = {};
  if (activeFilters.value.company_type?.length) {
    params.company_type = activeFilters.value.company_type;
  }
  if (activeFilters.value.status?.length) {
    params.status = activeFilters.value.status;
  }
  if (activeFilters.value.industry?.length) {
    params.industry = activeFilters.value.industry;
  }
  return params;
};

// Defined later in file, used by deleteCompanies above
const fetchCompanies = async (page, search, sort) => {
  clearSelection();
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortParam.value;
  const filterParams = buildFilterParams();

  updateURLParams(currentPage, currentSearch, currentSort, activeFilters.value);

  const requestParams = {
    page: currentPage,
    sort: currentSort,
    ...filterParams,
  };

  if (currentSearch) {
    await store.dispatch('companies/search', {
      search: currentSearch,
      ...requestParams,
    });
  } else {
    await store.dispatch('companies/get', requestParams);
  }
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchCompanies(1, query, sortParam.value);
}, DEBOUNCE_DELAY);

const onPageChange = page => {
  fetchCompanies(page, searchValue.value, sortParam.value);
};

const handleSort = ({ sort, order }) => {
  const newSortParam = order === '-' ? `-${sort}` : sort;
  fetchCompanies(1, searchValue.value, newSortParam);
};

const handleFiltersChange = filters => {
  activeFilters.value = filters;
  fetchCompanies(1, searchValue.value, sortParam.value);
};

const clearFilters = () => {
  activeFilters.value = {
    company_type: [],
    status: [],
    industry: [],
  };
  fetchCompanies(1, searchValue.value, sortParam.value);
};

const goToCompanyDetails = companyId => {
  router.push({
    name: 'companies_show',
    params: {
      accountId: route.params.accountId,
      companyId,
    },
  });
};

const createNewCompanyDialogRef = ref(null);

const openCreateNewCompanyDialog = async () => {
  await createNewCompanyDialogRef.value?.companiesFormRef?.resetValidation();
  createNewCompanyDialogRef.value?.dialogRef.open();
};

const onCreateCompany = async company => {
  try {
    await store.dispatch('companies/createCompany', company);
    createNewCompanyDialogRef.value?.onSuccess();
    useAlert(t('COMPANIES.CREATE.SUCCESS_MESSAGE'));
    fetchCompanies(1);
  } catch (error) {
    const data = error.response?.data;
    const backendMessage =
      data?.message ||
      (Array.isArray(data?.errors) ? data.errors.join(', ') : data?.errors) ||
      data?.error;
    useAlert(backendMessage || t('COMPANIES.CREATE.ERROR_MESSAGE'));
  }
};

// Parse filters from URL on mount
const parseFiltersFromURL = () => {
  const filters = {
    company_type: [],
    status: [],
    industry: [],
  };

  if (route.query.company_type) {
    filters.company_type = route.query.company_type.split(',');
  }
  if (route.query.status) {
    filters.status = route.query.status.split(',');
  }
  if (route.query.industry) {
    filters.industry = route.query.industry.split(',');
  }

  activeFilters.value = filters;
};

// Watch for company list changes to update selection
watch(
  companies,
  newCompanies => {
    const idsOnPage = newCompanies.map(company => company.id);
    selectedCompanyIds.value = selectedCompanyIds.value.filter(id =>
      idsOnPage.includes(id)
    );
  },
  { deep: true }
);

watch(hasSelection, value => {
  if (!value) {
    bulkDeleteDialogRef.value?.close?.();
  }
});

onMounted(() => {
  searchValue.value = searchQuery.value;
  parseFiltersFromURL();
  fetchCompanies();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <CompaniesListLayout
      :search-value="searchValue"
      :header-title="t('COMPANIES.HEADER')"
      :current-page="pageNumber"
      :total-items="Number(meta.count || 0)"
      :active-sort="activeSort"
      :active-ordering="activeOrdering"
      :active-filters="activeFilters"
      :is-fetching-list="isFetchingList"
      :show-pagination-footer="!isFetchingList && hasCompanies"
      @update:current-page="onPageChange"
      @update:sort="handleSort"
      @update:filters="handleFiltersChange"
      @clear-filters="clearFilters"
      @search="onSearch"
      @add="openCreateNewCompanyDialog"
    >
      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <CompaniesBulkActionBar
          v-if="hasSelection"
          :visible-company-ids="visibleCompanyIds"
          :selected-company-ids="selectedCompanyIds"
          :is-loading="isBulkActionLoading"
          @toggle-all="toggleSelectAll"
          @clear-selection="clearSelection"
          @delete-selected="openBulkDeleteDialog"
          @export-selected="exportSelectedCompanies"
        />

        <div
          v-if="companies.length === 0"
          class="flex flex-col items-center justify-center py-10 gap-2"
        >
          <span class="text-base text-n-slate-11">
            {{
              hasActiveFilters
                ? t('COMPANIES.EMPTY_STATE.NO_RESULTS')
                : t('COMPANIES.EMPTY_STATE.TITLE')
            }}
          </span>
          <button
            v-if="hasActiveFilters"
            class="text-sm text-n-blue-text hover:text-n-blue-11 underline"
            @click="clearFilters"
          >
            {{ t('COMPANIES_FILTERS.CLEAR_ALL') }}
          </button>
        </div>

        <CompaniesTable
          v-else
          class="pt-1 pb-6"
          :companies="companies"
          :selected-ids="selectedCompanyIds"
          :active-sort="activeSort"
          :active-ordering="activeOrdering"
          @show-company="goToCompanyDetails"
          @toggle-selection="toggleCompanySelection"
          @toggle-select-all="toggleSelectAll"
          @update:sort="handleSort"
        />

        <Dialog
          v-if="selectedCount"
          ref="bulkDeleteDialogRef"
          type="alert"
          :title="bulkDeleteDialogTitle"
          :description="bulkDeleteDialogDescription"
          :confirm-button-label="bulkDeleteDialogConfirmLabel"
          :is-loading="isBulkActionLoading"
          @confirm="deleteCompanies"
        />
      </template>
    </CompaniesListLayout>

    <CreateNewCompanyDialog
      ref="createNewCompanyDialogRef"
      @create="onCreateCompany"
    />
  </div>
</template>
